import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { Check, CheckCircle2, ChevronRight, MessageCircle } from "lucide-react";

export interface FeatureRoadmapItem {
  number: string;
  title: string;
  status: "done" | "active" | "next" | "idle";
}

interface ObsidianVioletSaaSProps {
  kicker?: string;
  headlineWhite?: string;
  headlineAccent?: string;
  features?: FeatureRoadmapItem[];
  activeFeatureIndex?: number;
}

export const ObsidianVioletSaaS: React.FC<ObsidianVioletSaaSProps> = ({
  kicker = "SERI FITUR • SEASON 1",
  headlineWhite = "Pembayaran beres,",
  headlineAccent = "tekan WhatsApp.",
  features = [
    { number: "01", title: "Cetak struk bluetooth", status: "done" },
    { number: "02", title: "Struk ke WhatsApp", status: "active" },
    { number: "03", title: "Denah meja F&B", status: "next" },
    { number: "04", title: "Manajemen inventori", status: "idle" },
    { number: "05", title: "Laporan laba kotor", status: "idle" },
  ],
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const sp = (delay: number) =>
    spring({
      frame: Math.max(0, frame - delay),
      fps,
      config: { damping: 16, stiffness: 140 },
    });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#0A0612",
        color: "#FFFFFF",
        fontFamily: "'Inter', sans-serif",
        overflow: "hidden",
      }}
    >
      {/* SOFT RADIAL VIOLET BACKLIGHT */}
      <div
        style={{
          position: "absolute",
          top: "35%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 700,
          height: 700,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(139, 92, 246, 0.28) 0%, rgba(88, 28, 135, 0.08) 50%, transparent 70%)",
          filter: "blur(60px)",
          pointerEvents: "none",
        }}
      />

      {/* TOP KICKER & BRAND CAPSULE (SAFE ZONE Y: 260px) */}
      <div
        style={{
          position: "absolute",
          top: 260,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          opacity: sp(0),
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            backgroundColor: "rgba(24, 18, 43, 0.8)",
            border: "1.5px solid rgba(139, 92, 246, 0.35)",
            padding: "8px 24px",
            borderRadius: 999,
            backdropFilter: "blur(12px)",
          }}
        >
          <span
            style={{
              fontSize: 18,
              fontWeight: 800,
              letterSpacing: 3,
              color: "#C4B5FD",
              textTransform: "uppercase",
            }}
          >
            {kicker}
          </span>
        </div>
      </div>

      {/* DUAL-TONE HEADLINE (Y: 340px) */}
      <div
        style={{
          position: "absolute",
          top: 340,
          left: 0,
          right: 0,
          textAlign: "center",
          padding: "0 60px",
          opacity: sp(4),
          transform: `translateY(${interpolate(sp(4), [0, 1], [24, 0])}px)`,
        }}
      >
        <div
          style={{
            fontSize: 58,
            fontWeight: 900,
            lineHeight: 1.1,
            letterSpacing: -0.5,
          }}
        >
          <span>{headlineWhite} </span>
          <span style={{ color: "#A855F7" }}>{headlineAccent}</span>
        </div>
      </div>

      {/* STAGE CONTAINER (WIDTH: 880px, Y: 500px - 1400px) */}
      <div
        style={{
          position: "absolute",
          top: 500,
          bottom: 480,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div style={{ width: 880, display: "flex", flexDirection: "column", gap: 14 }}>
          {features.map((item, idx) => {
            const isActive = item.status === "active";
            const isDone = item.status === "done";
            const isNext = item.status === "next";

            return (
              <div
                key={item.number}
                style={{
                  backgroundColor: isActive
                    ? "rgba(139, 92, 246, 0.16)"
                    : "rgba(24, 18, 43, 0.6)",
                  border: isActive
                    ? "2.5px solid #8B5CF6"
                    : "1.5px solid rgba(139, 92, 246, 0.2)",
                  boxShadow: isActive
                    ? "0 0 35px rgba(139, 92, 246, 0.35)"
                    : "none",
                  borderRadius: 22,
                  padding: "20px 28px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  transform: isActive ? "scale(1.03)" : "scale(1.0)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      backgroundColor: isDone
                        ? "#10B981"
                        : isActive
                        ? "#8B5CF6"
                        : "rgba(255, 255, 255, 0.08)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#FFFFFF",
                      fontWeight: 800,
                      fontSize: 18,
                    }}
                  >
                    {isDone ? <Check size={24} strokeWidth={3} /> : item.number}
                  </div>
                  <div
                    style={{
                      fontSize: 26,
                      fontWeight: isActive ? 800 : 600,
                      color: isActive ? "#FFFFFF" : "#CBD5E1",
                    }}
                  >
                    {item.title}
                  </div>
                </div>

                {isActive && (
                  <div
                    style={{
                      backgroundColor: "#8B5CF6",
                      color: "#FFFFFF",
                      padding: "6px 16px",
                      borderRadius: 999,
                      fontSize: 15,
                      fontWeight: 800,
                      letterSpacing: 1.5,
                    }}
                  >
                    AKTIF
                  </div>
                )}

                {isNext && (
                  <div
                    style={{
                      backgroundColor: "rgba(168, 85, 247, 0.2)",
                      border: "1px solid #A855F7",
                      color: "#C4B5FD",
                      padding: "6px 14px",
                      borderRadius: 999,
                      fontSize: 14,
                      fontWeight: 700,
                      letterSpacing: 1,
                    }}
                  >
                    BERIKUTNYA
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </AbsoluteFill>
  );
};
